import { Collapse, Divider, IconButton, Stack, styled } from '@ethings/ui-toolkit';
import { Box, Breakpoint, Dialog as MuiDialog, DialogContent as MuiDialogContent, DialogProps as MuiDialogProps, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { SxProps } from '@mui/system';
import { FC, forwardRef, PropsWithChildren, ReactNode, type JSX } from 'react';
import { useSmallScreen } from '../../hooks';
import { Icon } from '../icons';
import { ResponsiveText } from '../responsive-text';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

interface Props extends PropsWithChildren {
  actions?: React.ReactNode;
  'data-testid'?: string;
  disableCloseOnBackdropClick?: boolean;
  disableEscKey?: boolean;
  fullWidth?: boolean;
  hasCloseBtn?: boolean;
  maxWidth?: Breakpoint;
  onClose: () => void;
  open: boolean;
  scroll?: boolean;
  subtitle?: string;
  title?: string;
}

type DialogCloseReasons = 'escapeKeyDown' | 'backdropClick';

const Transition = (
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
): JSX.Element => {
  return <Slide direction="up" ref={ref} {...props} />;
};
const TransitionComponent = forwardRef(Transition);

export const Dialog: FC<Props> = ({
  actions,
  children,
  disableCloseOnBackdropClick,
  disableEscKey,
  fullWidth,
  hasCloseBtn,
  maxWidth,
  onClose,
  open,
  scroll,
  subtitle,
  title,
  ...props
}) => {
  const handleClose = (reason: DialogCloseReasons): void => {
    if (disableCloseOnBackdropClick && reason === 'backdropClick') return;
    onClose();
  };

  return <DialogRoot
    data-testid={props['data-testid']}
    disableEscapeKeyDown={disableEscKey}
    fullWidth={fullWidth}
    maxWidth={maxWidth}
    onClose={(_, reason) => handleClose(reason)}
    open={open}
    scroll={scroll ? 'paper' : undefined}
  >
    {hasCloseBtn && <DialogCloseButton onClick={onClose} />}
    <DialogInner data-testid={props['data-testid']}>
      {title && <DialogTitle title={title} subtitle={subtitle} />}
      {children && <DialogContent>{children}</DialogContent>}
      {actions && <DialogActions>{actions}</DialogActions>}
    </DialogInner>
  </DialogRoot>;
};

interface DialogRootProps extends MuiDialogProps {
  'data-testid'?: string;
}

export const DialogRoot: FC<DialogRootProps> = (props) => {
  return (
    <MuiDialog
      sx={{ maxHeight: '55vh', height: 'max-content', marginTop: 0, '& .MuiDialog-container': { height: 'auto' } }}
      TransitionComponent={TransitionComponent}
      {...props}
    />
  );
};

interface DialogCloseButtonProps {
  onClick: VoidFunction;
}

export const DialogCloseButton: FC<DialogCloseButtonProps> = ({ onClick }) => {
  return (
    <StyledIconButton
      aria-label="close"
      data-testid="close-button"
      onClick={onClick}
    >
      <Icon data-testid="close-icon" icon="CloseIcon" />
    </StyledIconButton>
  );
};

interface DialogInnerProps extends PropsWithChildren {
  sx?: SxProps;
}

export const DialogInner: FC<DialogInnerProps> = (props) => {
  return (
    <Stack
      direction='column'
      divider={<Divider orientation='horizontal' flexItem />}
      {...props}
    />
  );
};

interface DialogTitleProps {
  'data-testid'?: string;
  title: ReactNode;
  subtitle?: ReactNode;
}

export const DialogTitle: FC<DialogTitleProps> = ({ title, subtitle, ...props }) => {
  const smallScreen = useSmallScreen();

  const baseTitleSize = smallScreen ? '0.875rem' : '1rem';
  const baseSubtitleSize = smallScreen ? '0.875rem' : '1rem';

  return (
    <Stack spacing={1} m={2}>
      <ResponsiveText
        data-testid={props['data-testid'] ?? 'title'}
        variant={smallScreen ? 'body2' : 'body1'}
        responsiveOptions={{
          minFontSize: '0.8rem',
          maxFontSize: baseTitleSize,
          textLengthThreshold: 30,
        }}
        style={{ fontWeight: 'bold' }}
      >
        {title}
      </ResponsiveText>
      <Collapse in={!!subtitle}>
        <ResponsiveText
          data-testid="sub-title"
          variant={smallScreen ? 'body2' : 'body1'}
          responsiveOptions={{
            minFontSize: '0.75rem',
            maxFontSize: baseSubtitleSize,
            textLengthThreshold: 40,
          }}
        >
          {subtitle}
        </ResponsiveText>
      </Collapse>
    </Stack>
  );
};

interface DialogContentProps extends PropsWithChildren {
  'data-testid'?: string;
  scroll?: boolean;
  sx?: SxProps;
}

export const DialogContent: FC<DialogContentProps> = ({ children, scroll, sx, ...props }) => {
  return (
    <MuiDialogContent
      data-testid={props['data-testid'] ?? 'content'}
      sx={[{ p: 2, m: 0, maxHeight: scroll ? '80vh' : 'inherit' }, ...(sx ? [sx] : [])]}
    >
      {children}
    </MuiDialogContent>
  );
};

export const DialogActions: FC<PropsWithChildren> = ({ children }) => {
  const smallScreen = useSmallScreen();

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: smallScreen ? 1 : 2,
        '& > *': {
          flex: smallScreen ? '1 1 auto' : '0 0 auto',
          minWidth: smallScreen ? 0 : 'auto',
          maxWidth: smallScreen ? '48%' : 'none',
        },
      }}
    >
      {children}
    </Box>
  );
};
